import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';

const PullToRefresh = ({
  onRefresh,
  trigger = 70,
  maxPull = 120,
  className = '',
  contentClassName = '',
  style,
  contentStyle,
  disabled = false,
  scrollContainerRef,
  indicatorRenderer,
  children,
  ...rest
}) => {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [ignorePull, setIgnorePull] = useState(false);
  const startYRef = useRef(null);

  const isAtTop = () => {
    if (scrollContainerRef?.current) {
      return (scrollContainerRef.current.scrollTop || 0) <= 0;
    }
    const doc = document.documentElement;
    const top = (window.scrollY || doc.scrollTop || document.body.scrollTop || 0);
    return top <= 0;
  };

  const handleTouchStart = (e) => {
    if (disabled || refreshing || !isAtTop()) {
      setIgnorePull(true);
      startYRef.current = null;
      return;
    }
    setIgnorePull(false);
    startYRef.current = e.touches[0].clientY;
    setPullDistance(0);
    setPulling(false);
  };

  const handleTouchMove = (e) => {
    if (disabled || ignorePull || refreshing || startYRef.current == null) return;
    const dy = e.touches[0].clientY - startYRef.current;
    if (dy > 0) {
      const damped = Math.min(maxPull, dy * 0.6);
      setPulling(true);
      setPullDistance(damped);
    }
  };

  const handleTouchEnd = async () => {
    if (disabled || ignorePull || refreshing) return;
    if (pulling && pullDistance >= trigger) {
      try {
        setRefreshing(true);
        await onRefresh?.();
      } finally {
        setRefreshing(false);
        setPulling(false);
        setPullDistance(0);
        startYRef.current = null;
      }
      return;
    }
    setPulling(false);
    setPullDistance(0);
    startYRef.current = null;
  };

  const defaultIndicator = () => (
    <div
      className="flex items-center justify-center text-xs text-black dark:text-gray-300 pt-10"
      style={{ height: pulling || refreshing ? Math.min(48, pullDistance) : 0, transition: pulling ? 'none' : 'height 150ms ease' }}
    >
      {refreshing ? (
        <span className="flex items-center gap-2">
          <CircularProgress size={14} sx={{ color: '#d1d5db' }} />
          Refreshing...
        </span>
      ) : pulling ? (
        <span>{pullDistance >= trigger ? 'Release to refresh' : 'Pull to refresh'}</span>
      ) : null}
    </div>
  );

  return (
    <div
      {...rest}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'pan-y', ...(style || {}) }}
      className={className}
    >
      <div className="max-w-xl mx-auto" style={{ height: 0 }}>
        {indicatorRenderer ? indicatorRenderer({ pulling, pullDistance, refreshing, trigger }) : defaultIndicator()}
      </div>

      <div
        className={contentClassName}
        style={{ transform: pulling ? `translateY(${pullDistance}px)` : undefined, transition: pulling ? 'none' : 'transform 150ms ease', ...(contentStyle || {}) }}
      >
        {children}
      </div>
    </div>
  );
};

PullToRefresh.propTypes = {
  onRefresh: PropTypes.func,
  trigger: PropTypes.number,
  maxPull: PropTypes.number,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  style: PropTypes.object,
  contentStyle: PropTypes.object,
  disabled: PropTypes.bool,
  scrollContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  indicatorRenderer: PropTypes.func,
  children: PropTypes.node,
};

export default PullToRefresh;
